import { addDays, format, isSameDay } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'reactstrap';

const ITEM_HEIGHT = 40;

const DateTimePicker = ({ datesArray, timesArray, selectedDate, setSelectedDate, selectedTime, setSelectedTime }: any) => {
  const dateRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLDivElement>(null);
  const periodRef = useRef<HTMLDivElement>(null);

  const baseTimes = Array.from(new Set(timesArray.map((t: string) => t.slice(0, -3).trim())));
  const periods = ['AM', 'PM'];

  const [baseTime, setBaseTime] = useState(selectedTime?.slice(0, -3).trim());
  const [period, setPeriod] = useState(selectedTime?.slice(-2) || 'AM');

  useEffect(() => {
    const fullTime = `${baseTime} ${period}`;
    if (timesArray.includes(fullTime)) {
      setSelectedTime(fullTime);
    }
  }, [baseTime, period]);

  const getCenteredItemIndex = (container: HTMLDivElement) => {
    const children = Array.from(container.children) as HTMLDivElement[];
    const containerRect = container.getBoundingClientRect();
    const centerY = containerRect.top + containerRect.height / 2;

    let closestIndex = 0;
    let minDistance = Infinity;

    children.forEach((child, index) => {
      const rect = child.getBoundingClientRect();
      const itemCenter = rect.top + rect.height / 2;
      const distance = Math.abs(centerY - itemCenter);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  const scrollToIndex = (ref: React.RefObject<HTMLDivElement>, index: number) => {
    if (ref.current) {
      ref.current.scrollTo({
        top: index * ITEM_HEIGHT,
        behavior: 'smooth',
      });
    }
  };

  // --- Scroll Handlers with Debounced Snap-to-Center ---
  let timeScrollTimeout: NodeJS.Timeout;
  const onTimeScroll = () => {
    if (timeScrollTimeout) clearTimeout(timeScrollTimeout);
    timeScrollTimeout = setTimeout(() => {
      const container = timeRef.current;
      if (!container) return;
      const index = getCenteredItemIndex(container);
      const selectedBase = baseTimes[index];
      const combined = `${selectedBase} ${period}`;

      if (timesArray.includes(combined)) {
        setBaseTime(selectedBase);
        scrollToIndex(timeRef, index);
      }
    }, 100);
  };

  let periodScrollTimeout: NodeJS.Timeout;
  const onPeriodScroll = () => {
    if (periodScrollTimeout) clearTimeout(periodScrollTimeout);
    periodScrollTimeout = setTimeout(() => {
      const container = periodRef.current;
      if (!container) return;
      const index = getCenteredItemIndex(container);
      const selectedPeriod = periods[index];
      const combined = `${baseTime} ${selectedPeriod}`;

      if (timesArray.includes(combined)) {
        setPeriod(selectedPeriod);
        scrollToIndex(periodRef, index);
      }
    }, 100);
  };

  let dateScrollTimeout: NodeJS.Timeout;
  const onDateScroll = () => {
    if (dateScrollTimeout) clearTimeout(dateScrollTimeout);
    dateScrollTimeout = setTimeout(() => {
      const container = dateRef.current;
      if (!container) return;
      const index = getCenteredItemIndex(container);
      const newDate = datesArray[index];
      if (!isSameDay(newDate, selectedDate)) {
        setSelectedDate(newDate);
        scrollToIndex(dateRef, index);
      }
    }, 100);
  };

  const renderDateLabel = (date: Date) => {
    if (isSameDay(date, new Date())) return `Today, ${format(date, 'dd MMM')}`;
    if (isSameDay(date, addDays(new Date(), 1))) return `Tomorrow, ${format(date, 'dd MMM')}`;
    return format(date, 'dd MMM yyyy');
  };

  useEffect(() => {
    const dateIndex = datesArray.findIndex((d: any) => isSameDay(d, selectedDate));
    const timeIndex = baseTimes.findIndex((t: any) => t === baseTime);
    const periodIndex = periods.findIndex(p => p === period);

    scrollToIndex(dateRef, dateIndex);
    scrollToIndex(timeRef, timeIndex);
    scrollToIndex(periodRef, periodIndex);
  }, []);

  return (
    <Container className="mt-4">
      <h5 className="mb-3">Pickup Date & Time</h5>
      <Row>
        {/* Date Picker */}
        <Col xs="4" style={{ position: 'relative', borderRight: '1px solid #ddd' }}>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: ITEM_HEIGHT,
              marginTop: -ITEM_HEIGHT / 2,
              borderTop: '1px solid black',
              borderBottom: '1px solid black',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          <div
            ref={dateRef}
            onScroll={onDateScroll}
            style={{
              height: ITEM_HEIGHT * 5,
              overflowY: 'auto',
              paddingTop: ITEM_HEIGHT * 2,
              paddingBottom: ITEM_HEIGHT * 2,
            }}
            className="custom-date-selector"
          >
            {datesArray.map((date: any, idx: number) => {
              const isSelected = isSameDay(date, selectedDate);
              return (
                <div
                  key={idx}
                  style={{
                    height: ITEM_HEIGHT,
                    lineHeight: `${ITEM_HEIGHT}px`,
                    textAlign: 'center',
                    color: isSelected ? '#000' : '#aaa',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    fontSize: isSelected ? 14 : 12,
                  }}
                >
                  {renderDateLabel(date)}
                </div>
              );
            })}
          </div>
        </Col>

        {/* Time Picker */}
        <Col xs="4" style={{ position: 'relative', borderRight: '1px solid #ddd' }}>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: ITEM_HEIGHT,
              marginTop: -ITEM_HEIGHT / 2,
              borderTop: '1px solid black',
              borderBottom: '1px solid black',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          <div
            ref={timeRef}
            onScroll={onTimeScroll}
            style={{
              height: ITEM_HEIGHT * 5,
              overflowY: 'auto',
              paddingTop: ITEM_HEIGHT * 2,
              paddingBottom: ITEM_HEIGHT * 2,
            }}
            className="custom-time-selector"
          >
            {baseTimes.map((time: any, idx: number) => {
              const isSelected = time === baseTime;
              return (
                <div
                  key={idx}
                  style={{
                    height: ITEM_HEIGHT,
                    lineHeight: `${ITEM_HEIGHT}px`,
                    textAlign: 'center',
                    color: isSelected ? '#000' : '#aaa',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    fontSize: isSelected ? 14 : 12,
                  }}
                >
                  {time}
                </div>
              );
            })}
          </div>
        </Col>

        {/* AM / PM Picker */}
        <Col xs="4" style={{ position: 'relative' }}>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: ITEM_HEIGHT,
              marginTop: -ITEM_HEIGHT / 2,
              borderTop: '1px solid black',
              borderBottom: '1px solid black',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          <div
            ref={periodRef}
            onScroll={onPeriodScroll}
            style={{
              height: ITEM_HEIGHT * 5,
              overflowY: 'auto',
              paddingTop: ITEM_HEIGHT * 2,
              paddingBottom: ITEM_HEIGHT * 2,
            }}
            className="custom-time-selector"
          >
            {periods.map((p, idx) => {
              const isSelected = p === period;
              return (
                <div
                  key={idx}
                  style={{
                    height: ITEM_HEIGHT,
                    lineHeight: `${ITEM_HEIGHT}px`,
                    textAlign: 'center',
                    color: isSelected ? '#000' : '#aaa',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    fontSize: isSelected ? 14 : 12,
                  }}
                >
                  {p}
                </div>
              );
            })}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default DateTimePicker;
